import { useState } from 'react';
import { PERMISSIONS, type PermissionKey } from '@london-life/auth';
import { Button } from '@london-life/ui/components/button';
import { ConfirmDialog } from '@london-life/ui/admin/confirm-dialog';
import { api } from '../lib/api';
import { RecoveryCodes } from '../two-step/recovery-codes';
import { Badge } from '@london-life/ui/components/badge';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { useAdmin } from '../lib/admin-context';

// the signed-in admin's own details: who they are, their roles and exactly what they may do
export function AccountPage() {
  const { admin, twoStep } = useAdmin();
  const [replacing, setReplacing] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const codesLeft = codes ? codes.length : twoStep.recoveryCodesLeft;
  const permissions = admin.superAdmin ? (Object.keys(PERMISSIONS) as PermissionKey[]) : admin.permissions;
  return (
    <section>
      <PageHeader title="My account" description="Your admin profile and what your roles allow." />
      <div className="admin-panel">
        <h2>Profile</h2>
        <p>
          <strong>{admin.user.displayName ?? 'No name set'}</strong>
          <br />
          <span className="admin-muted">{admin.user.email}</span>{' '}
          {admin.user.emailVerified ? (
            <Badge variant="secondary">Email verified</Badge>
          ) : (
            <Badge variant="outline">Email not verified</Badge>
          )}
        </p>
      </div>
      <div className="admin-panel">
        <h2>Roles</h2>
        <div className="admin-tags">
          {admin.superAdmin ? (
            <Badge>Super admin</Badge>
          ) : (
            admin.roles.map((role) => (
              <Badge key={role.id} variant="secondary">
                {role.name}
              </Badge>
            ))
          )}
        </div>
      </div>
      <div className="admin-panel">
        <h2>What you can do</h2>
        <ul className="admin-muted">
          {permissions.map((permission) => (
            <li key={permission}>{PERMISSIONS[permission as PermissionKey] ?? permission}</li>
          ))}
        </ul>
      </div>
      <div className="admin-panel">
        <h2>Two-step login</h2>
        <p>
          <Badge variant="secondary">On</Badge>{' '}
          <span className="admin-muted">
            Each new sign-in needs a code from your authenticator app. You have {codesLeft} unused recovery{' '}
            {codesLeft === 1 ? 'code' : 'codes'}.
          </span>
        </p>
        {codesLeft <= 3 && !codes && (
          <p className="admin-muted">You are running low on recovery codes. Create new ones and save them.</p>
        )}
        {codes ? (
          <>
            <p className="admin-muted">Your new recovery codes. Save them now: this is the only time they are shown.</p>
            <RecoveryCodes codes={codes} />
          </>
        ) : (
          <Button variant="outline" onClick={() => setReplacing(true)}>
            Create new recovery codes
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={replacing}
        title="Create new recovery codes?"
        description="Your old recovery codes stop working straight away. You may be asked for a fresh code from your app first."
        confirmLabel="Create new codes"
        onClose={() => setReplacing(false)}
        onConfirm={async () => setCodes((await api.admin.twoStep.newRecoveryCodes()).recoveryCodes)}
      />
    </section>
  );
}
